import React, { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth, apiCall } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import {
  LayoutDashboard, Users, Upload, Phone, BarChart3, Settings, LogOut, Sun, Moon,
  Menu, X, Headphones, PhoneForwarded, ChevronRight, CheckCircle, Coffee, Timer,
  DollarSign, MessageCircle, Shield, Hash, Zap, FileSpreadsheet, Radio, Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const adminNav = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/agents", icon: Users, label: "Agents" },
  { to: "/clients", icon: Headphones, label: "All Clients" },
  { to: "/b2c-reports", icon: FileSpreadsheet, label: "B2C Reports" },
  { to: "/broadcast-reports", icon: Radio, label: "Broadcast Reports" },
  { to: "/upload", icon: Upload, label: "CSV Upload" },
  { to: "/auto-dialer", icon: PhoneForwarded, label: "Auto Dialer" },
  { to: "/dialpad", icon: Hash, label: "Dialpad" },
  { to: "/call-logs", icon: Phone, label: "Call Logs" },
  { to: "/conversions", icon: CheckCircle, label: "Conversions" },
  { to: "/reports", icon: BarChart3, label: "Reports" },
  { to: "/automations", icon: Zap, label: "Automations" },
  { to: "/salary", icon: DollarSign, label: "Salary" },
  { to: "/sub-admins", icon: Shield, label: "Sub-Admins" },
  { to: "/chat", icon: MessageCircle, label: "Team Chat" },
  { to: "/settings", icon: Settings, label: "Settings" },
];

const agentNav = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/clients", icon: Headphones, label: "My Clients" },
  { to: "/dialpad", icon: Hash, label: "Dialpad" },
  { to: "/call-logs", icon: Phone, label: "My Call Logs" },
  { to: "/convert", icon: CheckCircle, label: "Convert" },
  { to: "/b2c-reports", icon: FileSpreadsheet, label: "B2C Reports" },
  { to: "/broadcast-reports", icon: Radio, label: "Broadcast Reports" },
  { to: "/chat", icon: MessageCircle, label: "Team Chat" },
  { to: "/my-salary", icon: DollarSign, label: "My Salary" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [onBreak, setOnBreak] = useState(false);
  const [loginDuration, setLoginDuration] = useState("00:00:00");
  const location = useLocation();

  // Filter nav based on role and custom permissions
  const getNav = () => {
    if (user?.role === "agent") {
      if (user?.permissions && Array.isArray(user.permissions) && user.permissions.length > 0) {
        const agentPermMap = {
          dashboard: "/",
          clients: "/clients",
          dialpad: "/dialpad",
          call_logs: "/call-logs",
          conversions: "/convert",
          b2c_reports: "/b2c-reports",
          broadcast_reports: "/broadcast-reports",
          chat: "/chat",
          my_salary: "/my-salary"
        };
        return agentNav.filter(item => {
          if (item.to === "/") return user.permissions.includes("dashboard");
          return user.permissions.some(p => agentPermMap[p] === item.to);
        });
      }
      return agentNav;
    }
    if (user?.is_sub_admin && user?.permissions) {
      const permMap = {
        dashboard: "/",
        agents: "/agents",
        clients: "/clients",
        b2c_reports: "/b2c-reports",
        broadcast_reports: "/broadcast-reports",
        csv_upload: "/upload",
        auto_dialer: "/auto-dialer",
        dialpad: "/dialpad",
        call_logs: "/call-logs",
        conversions: "/conversions",
        reports: "/reports",
        automations: "/automations",
        salary: "/salary",
        settings: "/settings",
        chat: "/chat"
      };
      return adminNav.filter(item => {
        if (item.to === "/") return user.permissions.includes("dashboard");
        if (item.to === "/sub-admins") return false; // Only main admin
        return user.permissions.some(p => permMap[p] === item.to);
      });
    }
    return adminNav;
  };
  const nav = getNav();
  const currentPage = nav.find(n => n.to === "/" ? location.pathname === "/" : location.pathname.startsWith(n.to));
  const [chatUnread, setChatUnread] = useState(0);
  const prevUnreadRef = useRef(0);
  const audioRef = useRef(null);

  // Create notification sound once
  useEffect(() => {
    // Generate a proper notification beep using AudioContext
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const buffer = ctx.createBuffer(1, ctx.sampleRate * 0.3, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < buffer.length; i++) {
        data[i] = Math.sin(2 * Math.PI * 800 * (i / ctx.sampleRate)) * Math.exp(-3 * i / buffer.length);
      }
      audioRef.current = { ctx, buffer };
    } catch {}
  }, []);

  const playNotifSound = () => {
    try {
      if (audioRef.current) {
        const { ctx, buffer } = audioRef.current;
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.start();
      }
    } catch {}
  };

  // Fetch unread chat count + play notification sound
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const { data } = await apiCall("get", "/chat/contacts");
        const total = data.contacts.reduce((sum, c) => sum + (c.unread || 0), 0);
        if (total > prevUnreadRef.current) {
          playNotifSound();
        }
        prevUnreadRef.current = total;
        setChatUnread(total);
      } catch {}
    };
    fetchUnread();
    const i = setInterval(fetchUnread, 5000);
    return () => clearInterval(i);
  }, [user]);

  // Agent pending call popup - track responded calls to avoid duplicates
  const [pendingCall, setPendingCall] = useState(null);
  const [callResponse, setCallResponse] = useState({ response: "", notes: "" });
  const pollRef = useRef(null);
  const respondedCallsRef = useRef(new Set());
  const [agentTime, setAgentTime] = useState({ active: "0h 0m", break_used: "0h 0m", remaining: "8h 0m" });

  useEffect(() => {
    if (user?.role === "agent") {
      setOnBreak(user?.on_break || false);
      const poll = async () => {
        try {
          const { data } = await apiCall("get", "/agent/pending-call");
          if (data.pending && !respondedCallsRef.current.has(data.pending.call_id)) {
            if (!pendingCall || pendingCall.call_id !== data.pending.call_id) {
              setPendingCall(data.pending);
              setCallResponse({ response: "", notes: "" });
            }
          } else if (!data.pending) {
            setPendingCall(null);
          }
          const { data: st } = await apiCall("get", "/agent/status");
          setOnBreak(st.on_break || false);
          // Update cumulative times
          const fmtTime = (secs) => { const h = Math.floor(secs / 3600); const m = Math.floor((secs % 3600) / 60); return `${h}h ${m}m`; };
          setAgentTime({
            active: fmtTime(st.net_active_seconds || 0),
            break_used: fmtTime(st.total_break_seconds_today || 0),
            remaining: fmtTime(st.remaining_seconds || 0)
          });
        } catch {}
      };
      poll();
      pollRef.current = setInterval(poll, 5000);
      return () => clearInterval(pollRef.current);
    }
  }, [user]);

  useEffect(() => {
    const tick = () => {
      const lt = localStorage.getItem("login_time");
      if (lt) {
        const diff = Math.floor((Date.now() - new Date(lt).getTime()) / 1000);
        setLoginDuration(`${String(Math.floor(diff/3600)).padStart(2,"0")}:${String(Math.floor((diff%3600)/60)).padStart(2,"0")}:${String(diff%60).padStart(2,"0")}`);
      }
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [user]);

  // 5-min inactivity auto-logout for agents
  useEffect(() => {
    if (user?.role !== "agent") return;
    let inactivityTimer = null;
    let lastHeartbeat = 0;
    
    const doLogout = async () => {
      toast.error("Session expired due to inactivity");
      try { await apiCall("post", "/auth/logout"); } catch {}
      localStorage.removeItem("token");
      localStorage.removeItem("login_time");
      window.location.href = "/login";
    };
    
    const resetTimer = () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(doLogout, 5 * 60 * 1000);
      // Throttle heartbeat to once per 30s
      const now = Date.now();
      if (now - lastHeartbeat > 30000) {
        lastHeartbeat = now;
        apiCall("post", "/agent/heartbeat").catch(() => {});
      }
    };
    
    const events = ["mousedown", "keydown", "touchstart", "click"];
    events.forEach(e => document.addEventListener(e, resetTimer));
    resetTimer();
    
    return () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      events.forEach(e => document.removeEventListener(e, resetTimer));
    };
  }, [user]);

  const toggleBreak = async () => {
    try {
      if (onBreak) {
        const { data } = await apiCall("post", "/agent/end-break");
        setOnBreak(false);
        toast.success(`Break ended (${Math.floor((data.break_duration_seconds||0)/60)}m)`);
      } else {
        await apiCall("post", "/agent/start-break");
        setOnBreak(true);
        toast.info("Break started");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const submitPendingResponse = async () => {
    if (!callResponse.response) { toast.error("Select response"); return; }
    try {
      await apiCall("post", "/call-response", { client_id: pendingCall.client_user_id, call_id: pendingCall.call_id || "", response: callResponse.response, notes: callResponse.notes });
      toast.success("Response saved");
      // Track this call as responded so popup won't reopen
      respondedCallsRef.current.add(pendingCall.call_id);
      setPendingCall(null);
      setCallResponse({ response: "", notes: "" });
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background" data-testid="app-layout">
      {sidebarOpen && <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          <div className="h-16 flex items-center px-6 border-b border-border">
            <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center"><Phone className="w-4 h-4 text-primary-foreground" /></div><span className="font-bold text-lg tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>CallCRM</span></div>
            <button className="ml-auto lg:hidden" onClick={() => setSidebarOpen(false)}><X className="w-5 h-5" /></button>
          </div>
          <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
            {nav.map(item => (
              <NavLink key={item.to} to={item.to} end={item.to === "/"} onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 group ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                <item.icon className="w-4 h-4 shrink-0" /><span>{item.label}</span>
                {item.to === "/chat" && chatUnread > 0 && <Badge className="ml-auto bg-red-500 text-white text-[10px] h-5 min-w-5 rounded-full flex items-center justify-center p-0">{chatUnread}</Badge>}
                {item.to !== "/chat" && <ChevronRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />}
              </NavLink>
            ))}
          </nav>
          {(user?.role === "admin" || user?.permissions?.includes("extension_download")) && (
            <div className="p-3 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                className="w-full text-xs flex items-center justify-center gap-2 border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem("token");
                    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/extension/download`, {
                      headers: { Authorization: `Bearer ${token}` },
                      credentials: "include"
                    });
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "b2c-extension.zip";
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success("Chrome Extension download started");
                  } catch (err) {
                    toast.error("Download failed");
                  }
                }}
              >
                <Download className="w-3.5 h-3.5 text-primary" />
                <span>Download Extension</span>
              </Button>
            </div>
          )}
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border bg-card flex items-center px-4 lg:px-6 shrink-0">
          <button className="lg:hidden mr-3" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn"><Menu className="w-5 h-5" /></button>
          <div className="flex-1"><h1 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{currentPage?.label || "CRM"}</h1></div>
          <div className="flex items-center gap-2">
            {user?.role === "agent" && (
              <div className="hidden sm:flex items-center gap-2 mr-2 pr-2 border-r border-border" data-testid="agent-active-hours">
                <div className="text-right"><p className="text-[9px] text-muted-foreground uppercase leading-tight">Active</p><p className="text-xs font-bold text-green-600">{agentTime.active}</p></div>
                <div className="text-right"><p className="text-[9px] text-muted-foreground uppercase leading-tight">Break</p><p className="text-xs font-bold text-yellow-500">{agentTime.break_used}</p></div>
                <div className="text-right"><p className="text-[9px] text-muted-foreground uppercase leading-tight">Left</p><p className="text-xs font-bold text-orange-500">{agentTime.remaining}</p></div>
              </div>
            )}
            {onBreak && <Badge className="bg-yellow-500 text-black text-[10px]">BREAK</Badge>}
            {user?.role === "agent" && (
              <Button variant={onBreak ? "default" : "outline"} size="sm" className={`h-8 text-xs ${onBreak ? "bg-yellow-500 hover:bg-yellow-600 text-black" : ""}`} onClick={toggleBreak} data-testid="break-toggle-btn"><Coffee className="w-3 h-3 mr-1" />{onBreak ? "End" : "Break"}</Button>
            )}
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-md h-8 w-8" data-testid="theme-toggle">{theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</Button>
            <div className="flex items-center gap-2 pl-2 border-l border-border">
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">{user?.name?.[0]?.toUpperCase()}</div>
              <span className="text-sm font-medium hidden sm:block">{user?.name}</span>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={logout} data-testid="logout-btn"><LogOut className="w-4 h-4" /></Button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-6"><Outlet /></main>
      </div>

      {/* Agent Pending Call Popup (from auto dialer) */}
      <Dialog open={!!pendingCall} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" data-testid="agent-pending-call-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Phone className="w-5 h-5 text-green-500 animate-pulse" /> Incoming Call - {pendingCall?.client_user_id}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-md bg-green-500/10 border border-green-500/20"><p className="text-sm font-medium text-green-700 dark:text-green-400">Auto Dialer call connected. Submit response:</p></div>
            <div className="grid grid-cols-3 gap-2">
              {["interested", "not_interested", "follow_up", "not_connected", "converted"].map(r => (
                <Button key={r} variant={callResponse.response === r ? "default" : "outline"} size="sm" onClick={() => setCallResponse({ ...callResponse, response: r })} className="capitalize text-xs" data-testid={`pending-response-${r}`}>{r.replace(/_/g, " ")}</Button>
              ))}
            </div>
            <Textarea placeholder="Remarks..." value={callResponse.notes} onChange={e => setCallResponse({ ...callResponse, notes: e.target.value })} rows={2} />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPendingCall(null)}>Skip</Button><Button onClick={submitPendingResponse} data-testid="save-pending-response-btn">Save Response</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
